import React, { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCogs, faTree, faChartLine, faHome, faMagic } from "@fortawesome/free-solid-svg-icons";
import msIdle from "../assets/ModeSelectorIdle.svg";
import ms1 from "../assets/ModeSelector1.svg"; // Optischedule (Balanced)
import ms2 from "../assets/ModeSelector2.svg"; // Autonomous
import ms3 from "../assets/ModeSelector3.svg"; // CO2
import ms4 from "../assets/ModeSelector4.svg"; // Custom
import ms0 from "../assets/ModeSelector0.svg"; // Trading



const SVG_BY_VARIANT = { 0: ms0, 1: ms1, 2: ms2, 3: ms3, 4: ms4};
const VARIANT_TO_ENUM = { 0: "Trading", 1: "Optischedule", 2: "Autonomous", 3: "CO2", 4: "Custom" };

const prefTypeToVariant = (t) =>
  t === "Trading"
    ? 0
    : t === "Optischedule"
    ? 1
    : t === "Autonomous"
    ? 2
    : t === "CO2"
    ? 3
    : t === "Custom"
    ? 4
    : null;

export default function SteeringModeSelector({
  preferredType = "Idle",
  isSteeringOn = false,
  onAttemptPick,
  tradingId=null,
  className = "",
  style,
}) {
  const { t } = useTranslation();

  const initial = useMemo(() => prefTypeToVariant(preferredType), [preferredType]);
  const [selected, setSelected] = useState(initial);

  // keep in sync with backend (after refetches)
  useEffect(() => setSelected(prefTypeToVariant(preferredType)), [preferredType]);

  const svgSrc =
    !isSteeringOn || selected == null ? msIdle : SVG_BY_VARIANT[selected] ?? msIdle;

  const options = [
    { variant: 0, key: "SteeringPref.Trading", icon: faChartLine },
    { variant: 1, key: "SteeringPref.Optischedule", icon: faMagic },
    { variant: 2, key: "SteeringPref.Autonomous", icon: faHome },
    { variant: 3, key: "SteeringPref.CO2", icon: faTree },
    { variant: 4, key: "SteeringPref.Custom", icon: faCogs },
  ];

  async function handleClick(variant, disabled) {
    if (disabled || variant === selected) return;

    const enumValue = VARIANT_TO_ENUM[variant];
    if (!enumValue) return;

    const ok = onAttemptPick ? await onAttemptPick(enumValue) : true;
    if (ok) setSelected(variant);
  }

  return (
    <div className={`mb-3 smp2 ${className}`} style={style}>

      <div className="smp2-info">
        <span className="smp2-info-icon" aria-hidden>
          i
        </span>
        <span className="smp2-info-text">
          {!isSteeringOn
            ? t("SteeringPref.TurnOnToChoose")
            : selected == null
            ? t("SteeringPref.IdleSelected")
            : selected === 0
            ? tradingId
              ? t("SteeringPref.TraderSelected", { id: tradingId})
              : t("SteeringPref.TraderSelectedFallback")
            : selected === 1
            ? t("SteeringPref.OptiSelected")
            : selected === 2
            ? t("SteeringPref.AutoSelected")
            : selected === 3
            ? t("SteeringPref.CO2Selected")
            : selected === 4
            ? t("SteeringPref.CustomSelected")
            : t("SteeringPref.Unavailable")}
        </span>
      </div>

      <div className="smp2-svg" aria-hidden>
        <img src={svgSrc} alt="" draggable={false} />
      </div>

      <div className="smp2-opts" role="radiogroup" aria-label={t("SteeringPref.GroupLabel")}>
        {options.map((opt, i) => {
          const disabled = !isSteeringOn;
          const isActive = selected === opt.variant;

          return (
            <button
              key={opt.variant}
              type="button"
              role="radio"
              aria-checked={isActive}
              aria-disabled={disabled}
              className={[
                "smp2-option",
                isActive ? "is-active" : "",
                disabled ? "is-disabled" : "",
              ].join(" ").trim()}
              disabled={disabled}
              data-row={i + 1}
              onClick={() => handleClick(opt.variant, disabled)}
            >
              <div className="mode-labels">
                <FontAwesomeIcon fixedWidth className="mr-3" style={{ opacity: disabled ? 0.35 : !isActive ? 0.7 : 1 }} icon={opt.icon} color="var(--brand-navy)" size="1x" />
                <span>{t(opt.key)}</span>
              </div>
            </button>
          );
        })}
      </div>
    </div>
  );
}
